import { computed, Injectable, signal } from '@angular/core';

export type SimulationAnswerValue = number | string;
export type SimulationQuestionKind = 'option' | 'text';

export interface SimulationOption {
  label: string;
  value: SimulationAnswerValue;
}

export interface SimulationQuestion {
  id: string;
  kind: SimulationQuestionKind;
  label: string;
  options?: readonly SimulationOption[];
}

export interface SimulationQuestionGate {
  questionId: string;
  values?: readonly SimulationAnswerValue[];
}

export interface SimulationStep {
  id: string;
  questionIds: readonly string[];
  title: string;
}

export interface SimulationSummaryEntry {
  displayValue: string;
  questionId: string;
  questionLabel: string;
}

const unansweredDisplayValue = '-';

// TODO: add real question catalog when the form PBI lands
const simulationQuestions: Readonly<Record<string, SimulationQuestion>> = {};

// TODO: add decision-tree gates when the form PBI lands
const simulationQuestionGates: Readonly<Record<string, readonly SimulationQuestionGate[]>> = {};

// TODO: replace with design titles for steps 2–4 and wire questionIds
const simulationSteps: readonly SimulationStep[] = [
  { id: 'step-1', questionIds: [], title: 'Perfil del cliente' },
  { id: 'step-2', questionIds: [], title: 'Paso 2' },
  { id: 'step-3', questionIds: [], title: 'Paso 3' },
  { id: 'step-4', questionIds: [], title: 'Paso 4' },
];

@Injectable({ providedIn: 'root' })
export class SimulationFlowService {
  readonly steps = simulationSteps;
  private readonly answers = signal<Readonly<Record<string, SimulationAnswerValue>>>({});
  private readonly currentStepIndex = signal(0);
  readonly currentStepNumber = computed(() => this.currentStepIndex() + 1);
  readonly currentStepTitle = computed(() => this.steps[this.currentStepIndex()]?.title ?? '');
  readonly currentVisibleQuestionIds = computed(() => this.getCurrentVisibleQuestionIds());
  readonly currentStepAnswers = computed(() => this.getCurrentStepAnswers());
  readonly progressRatio = computed(() => this.currentStepNumber() / this.totalSteps);
  readonly totalSteps = this.steps.length;

  clearAnswer(questionId: string): void {
    if (!(questionId in simulationQuestions)) {
      return;
    }
    this.answers.update((current) => {
      const next = { ...current };
      delete next[questionId];
      return this.pruneAnswers(next);
    });
  }

  getAnswer(questionId: string): SimulationAnswerValue | undefined {
    return this.answers()[questionId];
  }

  getAnswers(): Readonly<Record<string, SimulationAnswerValue>> {
    return this.answers();
  }

  getQuestion(questionId: string): SimulationQuestion | undefined {
    return simulationQuestions[questionId];
  }

  getVisibleQuestionIds(): readonly string[] {
    return this.currentVisibleQuestionIds();
  }

  goToStep(stepNumber: number): void {
    const index = stepNumber - 1;
    if (index < 0 || index >= this.totalSteps) {
      return;
    }
    this.currentStepIndex.set(index);
  }

  next(): void {
    this.goToStep(this.currentStepNumber() + 1);
  }

  previous(): void {
    this.goToStep(this.currentStepNumber() - 1);
  }

  setAnswer(questionId: string, value: SimulationAnswerValue): void {
    const question = simulationQuestions[questionId];
    if (!question) {
      return;
    }
    if (question.kind === 'text') {
      if (typeof value !== 'string') {
        return;
      }
    } else {
      const isValid = question.options?.some((option) => option.value === value);
      if (!isValid) {
        return;
      }
    }
    this.answers.update((current) => this.pruneAnswers({ ...current, [questionId]: value }));
  }

  private getCurrentStepAnswers(): readonly SimulationSummaryEntry[] {
    const stored = this.answers();
    return this.currentVisibleQuestionIds().flatMap((questionId) => {
      const question = simulationQuestions[questionId];
      if (!question) {
        return [];
      }
      return [{
        displayValue: this.isAnswered(stored, questionId) ?
          this.resolveDisplayValue(question, stored[questionId]) :
          unansweredDisplayValue,
        questionId,
        questionLabel: question.label,
      }];
    });
  }

  private getCurrentVisibleQuestionIds(): readonly string[] {
    const step = this.steps[this.currentStepIndex()];
    if (!step) {
      return [] as readonly string[];
    }
    return this.visibleQuestionIdsForStep(step, this.answers());
  }

  private isAnswered(
    answers: Readonly<Record<string, SimulationAnswerValue>>,
    questionId: string,
  ): boolean {
    const value = answers[questionId];
    return value !== undefined && value !== '';
  }

  private isQuestionVisible(
    questionId: string,
    answers: Readonly<Record<string, SimulationAnswerValue>>,
  ): boolean {
    const gates = simulationQuestionGates[questionId] ?? [];
    return gates.every((gate) => {
      if (!this.isAnswered(answers, gate.questionId)) {
        return false;
      }
      if (!gate.values || gate.values.length === 0) {
        return true;
      }
      return gate.values.includes(answers[gate.questionId]);
    });
  }

  private pruneAnswers(
    answers: Readonly<Record<string, SimulationAnswerValue>>,
  ): Readonly<Record<string, SimulationAnswerValue>> {
    let current = answers;
    let changed = true;
    while (changed) {
      changed = false;
      const visibleIds = new Set<string>();
      for (const step of simulationSteps) {
        for (const questionId of this.visibleQuestionIdsForStep(step, current)) {
          visibleIds.add(questionId);
        }
      }
      const next: Record<string, SimulationAnswerValue> = {};
      for (const [questionId, value] of Object.entries(current)) {
        if (visibleIds.has(questionId)) {
          next[questionId] = value;
        } else {
          changed = true;
        }
      }
      current = next;
    }
    return current;
  }

  private resolveDisplayValue(
    question: SimulationQuestion,
    value: SimulationAnswerValue,
  ): string {
    if (question.kind === 'text') {
      return String(value);
    }
    return question.options?.find((option) => option.value === value)?.label ?? String(value);
  }

  private visibleQuestionIdsForStep(
    step: SimulationStep,
    answers: Readonly<Record<string, SimulationAnswerValue>>,
  ): readonly string[] {
    return step.questionIds.filter((questionId) => this.isQuestionVisible(questionId, answers));
  }
}
